/**
 * Calendar API
 *
 * Low-level API calls for Calendar, Availability, and Override entities.
 * This is the infrastructure layer - handles HTTP requests.
 *
 * All calendar operations are now service-centric under /services/:service_id/*
 * The legacy /calendars/* controller has been removed.
 */
import api from './axios';
function stripClientAuthority(payload) {
    const { created_by, updated_by, org_id, ...cleanPayload } = payload;
    void created_by;
    void updated_by;
    void org_id;
    return cleanPayload;
}
// =============================================================================
// CALENDAR API
// =============================================================================
export const calendarApi = {
    // ---------------------------------------------------------------------------
    // SERVICE-CENTRIC ENDPOINTS (PRIMARY)
    // ---------------------------------------------------------------------------
    /**
     * Get calendar and availability for a service
     */
    getServiceCalendar(serviceId) {
        return api.get(`/services/${serviceId}/calendar`);
    },
    /**
     * Create calendar for a service (calendar auto-created with service in new API)
     * @deprecated Calendars are now auto-created when service is created
     */
    createServiceCalendar(serviceId, dto) {
        return api.post(`/services/${serviceId}/calendar`, stripClientAuthority(dto));
    },
    /**
     * Get calendar schedule for a specific date
     * Returns the active schedule (regular availability or override) for the given date
     */
    getScheduleByDate(serviceId, date) {
        return api.get(`/services/${serviceId}/schedule`, {
            params: { date }
        });
    },
    /**
     * Get time slots for a specific date
     * @param view - 'booked' (default), 'available', or 'detailed'
     */
    getSlotsByDate(serviceId, date, view = 'booked') {
        return api.get(`/services/${serviceId}/slots`, {
            params: { date, view }
        });
    },
    /**
     * Get availability for a date range
     * Returns a map of dates with availability status and slot details
     */
    getAvailabilityRange(serviceId, params) {
        return api.get(`/services/${serviceId}/availability`, { params });
    },
    /**
     * Check availability for a potential booking (pre-booking validation)
     * Validates: schedule exists, reservations allowed, time constraints, capacity
     */
    checkAvailability(serviceId, params) {
        return api.get(`/services/${serviceId}/availability`, { params });
    },
    // ---------------------------------------------------------------------------
    // AVAILABILITY RULES (Weekly schedules)
    // ---------------------------------------------------------------------------
    /**
     * Get all weekly availability rules for a service
     */
    getAvailabilityRules(serviceId) {
        return api.get(`/services/${serviceId}/calendar/availability`);
    },
    /**
     * Get active bookings that no longer fit current persisted availability.
     */
    getOutOfServiceBookings(serviceId) {
        return api.get(`/services/${serviceId}/calendar/out-of-service-bookings`);
    },
    /**
     * Upsert (replace all) availability rules
     * Deletes all existing rules and creates new ones
     */
    upsertAvailability(serviceId, availability) {
        return api.put(`/services/${serviceId}/calendar/availability`, { availability });
    },
    /**
     * Update a specific availability rule
     */
    updateAvailability(serviceId, availabilityId, dto) {
        return api.patch(`/services/${serviceId}/calendar/availability/${availabilityId}`, stripClientAuthority(dto));
    },
    /**
     * Delete a specific availability rule
     */
    deleteAvailability(serviceId, availabilityId) {
        return api.delete(`/services/${serviceId}/calendar/availability/${availabilityId}`);
    },
    // ---------------------------------------------------------------------------
    // OVERRIDES (Date-range specific schedules)
    // ---------------------------------------------------------------------------
    /**
     * Get all overrides for a service, optionally filtered by date range
     */
    getOverrides(serviceId, params) {
        return api.get(`/services/${serviceId}/calendar/overrides`, { params });
    },
    /**
     * Create a new override for a service
     */
    createOverride(serviceId, dto) {
        return api.post(`/services/${serviceId}/calendar/overrides`, stripClientAuthority(dto));
    },
    /**
     * Update a specific override
     */
    updateOverride(serviceId, overrideId, dto) {
        return api.patch(`/services/${serviceId}/calendar/overrides/${overrideId}`, stripClientAuthority(dto));
    },
    /**
     * Delete a specific override
     */
    deleteOverride(serviceId, overrideId) {
        return api.delete(`/services/${serviceId}/calendar/overrides/${overrideId}`);
    },
    // ---------------------------------------------------------------------------
    // USER-CENTRIC ENDPOINTS
    // ---------------------------------------------------------------------------
    /**
     * Get all calendars for a user
     */
    getUserCalendars(userId) {
        return api.get(`/users/${userId}/calendars`);
    },
};
// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default calendarApi;
